import { KIA_ID } from '@/constants/home/home.constants';
import {
  availableUsedTypes,
  VEHICLE_STATUS_MAP,
} from '@/constants/used-cars/used-cars.constants';
import { FiltersTypes } from '@/types/filters/filters.types';
import {
  BodyTypesReq,
  BrandCountReq,
  BrandReq,
  DealershipRequest,
  LocationReq,
} from '@/types/request/request.types';
import { Vehicle, VehicleStatus } from '@/types/used-cars/used-cars.types';

export const mapBrandsPaths = (brands: any[]) => {
  return filterBrandsRanges(brands).map(({ name }) => ({
    params: {
      brand: name.toLowerCase(),
      carType: 'used',
    },
  }));
};

export const mapBrandsModelsPaths = (brandsRanges: any[]) => {
  return filterBrandsRanges(brandsRanges)
    .map((brand: any) =>
      brand.range_ids?.map((model: any) => ({
        params: {
          brand: brand.name.toLowerCase(),
          model: model.name.toLowerCase(),
          carType: 'used',
        },
      })),
    )
    .flat();
};

export const mapProvincesPaths = (provinces: any[]) => {
  return provinces.map(({ name }) => ({
    params: {
      province: name.toLowerCase(),
    },
  }));
};

export const mapBodyTypesPaths = (bodyTypes: any[]) => {
  return bodyTypes.map((bodyType) => ({
    params: {
      type: bodyType.name.toLowerCase().replace(/ /g, '-'),
      carType: 'used',
    },
  }));
};

export const mapProvincesCitiesPaths = (provincesCities: any[]) => {
  return provincesCities
    .map((brand) =>
      brand.cities?.map((city: any) => ({
        params: {
          province: brand.name.toLowerCase(),
          city: city.name.toLowerCase(),
          carType: 'used',
        },
      })),
    )
    .flat();
};

export const mapProvincesDealershipsPaths = (
  provinces: any,
  dealerships: any[],
) => {
  return provinces
    .map((province: any) =>
      dealerships.reduce((result, dealership) => {
        if (dealership.province === province.id) {
          result.push({
            params: {
              province: province.name.toLowerCase(),
              dealership: dealership.name.toLowerCase(),
              carType: 'used',
            },
          });
        }

        return result;
      }, [] as any[]),
    )
    .flat();
};

export const filterBrandsRanges = (
  brandAndRanges: any[],
  dealershipId?: string,
) => {
  return brandAndRanges
    .map((brand) => {
      const vehicles = brand.vehicles.filter((brand: any) =>
        filterByDealershipStatusAndType(brand, dealershipId),
      );
      const range_ids =
        brand.range_ids &&
        brand.range_ids
          .map((range: any) => {
            const vehicles =
              range.vehicles &&
              range.vehicles.filter((range: any) =>
                filterByDealershipStatusAndType(range, dealershipId),
              );

            return {
              ...range,
              vehicles,
            };
          })
          .filter((range: any) => range.vehicles && range.vehicles.length > 0);

      return {
        ...brand,
        vehicles,
        range_ids,
      };
    })
    .filter((brand) => brand.vehicles.length > 0);
};

export const mapBrandRangeCountResponse = (brandAndRanges: BrandCountReq[]) => {
  return brandAndRanges.map((brand) => {
    const { vehicles: brandVehicles, ...brandWithoutVehicles } = brand as any;

    return {
      ...brandWithoutVehicles,
      vehicleCount: brandVehicles.length,
      type: FiltersTypes.parent,
      fullName: brand.name,
      hasActiveSpecialDates:
        brand?.specials?.some(
          (special) =>
            special?.status === 'approved' &&
            new Date(special?.start_date) <= new Date() &&
            new Date(special?.end_date) >= new Date(),
        ) ?? false,
      specials: brand?.specials?.map((special) => special?.id) ?? [],
      hasApprovedSpecials:
        brand?.specials?.some((special) => special?.status === 'approved') ??
        false,
      children: brand.range_ids.map((range) => {
        const { vehicles: rangeVehicles, ...rangeWithoutVehicles } = range;

        return {
          ...rangeWithoutVehicles,
          id: `${brand.id}-${range.id}`,
          type: FiltersTypes.child,
          fullName: `${brand.name} ${range.name}`,
          code: range.code,
          parentId: brand.id,
          vehicleCount: rangeVehicles.length,
        };
      }),
    };
  });
};

export const mapBrandAndRangesResponse = (
  brandAndRanges: any[],
  dealershipId?: string,
) => {
  return filterBrandsRanges(brandAndRanges, dealershipId)
    .map(mapBrands)
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const mapBrands = (brand: BrandReq) => ({
  id: brand.id,
  type: FiltersTypes.parent,
  name: brand.name,
  fullName: brand.name,
  logo: brand.logo,
  code: brand.code,
  isChecked: false,
  hasActiveSpecialDates:
    brand?.specials?.some(
      (special) =>
        special?.status === 'approved' &&
        new Date(special?.start_date) <= new Date() &&
        new Date(special?.end_date) >= new Date(),
    ) ?? false,
  specials: brand?.specials?.map((special) => special?.id) ?? [],
  hasApprovedSpecials:
    brand?.specials?.some((special) => special?.status === 'approved') ?? false,
  isOpened: false,
  vehicles: brand.vehicles,
  children: brand.range_ids
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((range) => {
      return {
        id: `${brand.id}-${range.id}`,
        type: FiltersTypes.child,
        name: range.name,
        popularity: mapToGetPopularity(range as any),
        fullName: `${brand.name} ${range.name}`,
        code: range.code,
        isChecked: false,
        parentId: brand.id,
        vehicles: range.vehicles,
      };
    }),
});

const filterByDealershipStatusAndType = (
  vehicle: any,
  dealershipId?: string,
  status = VEHICLE_STATUS_MAP[VehicleStatus.active],
) => {
  if (vehicle.status !== status) {
    return false;
  }

  if (!availableUsedTypes.includes(vehicle.type)) {
    return false;
  }

  if (!dealershipId) {
    return true;
  }

  return vehicle.dealership_id === dealershipId;
};

export const mapCarTypesResponse = (
  data: BodyTypesReq[],
  type: FiltersTypes,
) => {
  if (!data) {
    return [];
  }

  return data?.map((bodyType) => ({
    id: bodyType.id,
    name: bodyType.name,
    fullName: bodyType.name,
    isSelected: false,
    type,
  }));
};

export const mapProvincesResponse = (
  locations: LocationReq[],
  dealershipId?: string,
) => {
  const allChecked = !dealershipId;

  return locations.map((province) => {
    const provinceChildren = (province as any).dealerships?.map(
      (dealership: DealershipRequest) => {
        const isChecked = Boolean(
          dealershipId && dealership.id === dealershipId,
        );

        return {
          id: dealership.id,
          type: FiltersTypes.child,
          name: dealership.name,
          isChecked: allChecked || isChecked,
          opened: false,
          fullName: dealership.name,
          code: dealership.name,
          vehicles: null,
          parentId: province.id,
          latitude: dealership.latitude,
          longitute: dealership.longitute,
        };
      },
    );

    return {
      id: province.id,
      type: FiltersTypes.parent,
      name: province.name,
      image: province.image,
      isChecked:
        allChecked || Boolean(dealershipId && province.id === dealershipId),
      opened: false,
      fullName: province.name,
      code: province.name,
      vehicles: null,
      latitude: province.latitude || null,
      longitute: province.longitute || null,
      children: provinceChildren,
    };
  });
};
export const mapLocationsResponse = (locations: LocationReq[]) => {
  return locations.map((province: any) => {
    return {
      id: province.id,
      type: FiltersTypes.parent,
      name: province.name,
      image: province.image,
      isChecked: true,
      isOpened: false,
      fullName: province.name,
      code: province.name,
      vehicles: null,
      latitude: province.latitude || null,
      longitute: province.longitute || null,
      children: province.cities.map((city: LocationReq) => {
        return {
          id: city.id,
          type: FiltersTypes.child,
          name: city.name,
          isChecked: true,
          isOpened: false,
          fullName: city.name,
          code: city.name,
          vehicles: null,
          parentId: province.id,
        };
      }),
    };
  });
};

export const mapDealershipsResponse = (dealerships: any[]) => {
  return dealerships.map((dealership: any) => {
    return {
      id: dealership.id,
      name: dealership.name,
      code: dealership.name,
      vehicles: dealership.vehicles,
      province: dealership.province,
    };
  });
};

export const mapBodyTypesResponse = (
  data: BodyTypesReq[],
  type: FiltersTypes,
  visibleData: { id: number }[],
) => {
  return data
    .sort((a, b) => a.id - b.id)
    .map((bodyType) => {
      const vehicles =
        bodyType?.vehicles &&
        bodyType.vehicles.filter((bodyType) =>
          filterByDealershipStatusAndType(bodyType),
        );

      return {
        id: bodyType.id,
        name: bodyType.name,
        value: bodyType.value,
        fullName: bodyType.name,
        dealer_name_body_type: bodyType.dealer_name_body_type,
        isSelected: false,
        image: bodyType.image,
        isDisabled: !visibleData.find((item) => item.id === bodyType.id),
        vehicles,
        type,
      };
    });
};

export const mapToGetPopularity = (range: { vehicles: any[] }) => {
  const { total, count } = range.vehicles.reduce(
    (a, b) => {
      a.total += parseFloat(b.popularity);
      a.count++;
      return a;
    },
    { total: 0, count: 0 },
  );

  return parseFloat((total / count).toFixed(2));
};

export const makeKiaFirst = (brandAndRanges: any[]) => {
  return [...brandAndRanges].sort((a, b) =>
    a.id === KIA_ID ? -1 : b.id === KIA_ID ? 1 : 0,
  );
};

export const makeKiaFirstVehicles = (vehicles: Vehicle[]) => {
  return vehicles.sort((a, b) =>
    a.brand === 'KIA' ? -1 : b.brand === 'KIA' ? 1 : 0,
  );
};

export const getOnlyKia = (brands: any[]) => {
  return [...brands].filter((brand) => brand.id === KIA_ID);
};
